// "Continue with Google" — shared by SignIn and SignUp (both skins: the app
// card and the dark glonet.ai funnel shell wrap the same white card, so one
// Google-branding-compliant button serves both).
//
// The OAuth redirect wipes React state, so the funnel context (from /
// invitationCode / eventId) rides Amplify's customState and is replayed by
// the Hub listener in App.js. First-time Google sign-ins on an email that
// already has a password account are LINKED server-side (preSignup lambda)
// and the aborted attempt auto-retries once — see App.js.
import React from "react";
import { Auth } from "aws-amplify";

export const GOOGLE_RETRY_KEY = "g-signin-retry";
export const GOOGLE_STATE_KEY = "g-signin-state";

// The linked-account retry: same provider, same return address.
export function retryGoogleSignIn() {
  let customState;
  try { customState = sessionStorage.getItem(GOOGLE_STATE_KEY) || undefined; } catch (e) { customState = undefined; }
  Auth.federatedSignIn({ provider: "Google", customState });
}

export function startGoogleSignIn(locationState) {
  const { from, invitationCode, eventId } = locationState || {};
  let customState;
  try {
    customState = JSON.stringify({ from, invitationCode, eventId });
  } catch (e) {
    customState = undefined;
  }
  sessionStorage.removeItem(GOOGLE_RETRY_KEY);
  // Kept for the linked-account auto-retry (App.js): that second sign-in used
  // to start with NO customState, so the return address — a Water Cooler
  // invite link, say — was lost and the member landed on the default page.
  try {
    if (customState) sessionStorage.setItem(GOOGLE_STATE_KEY, customState);
    else sessionStorage.removeItem(GOOGLE_STATE_KEY);
  } catch (e) { /* private mode */ }
  Auth.federatedSignIn({ provider: "Google", customState });
}

// `blocked` gates the button the way the signup form gates its own submit:
// a first Google sign-in CREATES an account, so on the signup page it must not
// be usable until the member has accepted the terms. onBlockedClick surfaces
// the reason instead of the click silently doing nothing. `beforeStart` runs
// just before the redirect (used to park the accepted terms version, since the
// redirect wipes React state).
export default function GoogleSignInButton({
  locationState, label = "Continue with Google",
  blocked = false, onBlockedClick, beforeStart,
}) {
  const handleClick = () => {
    if (blocked) {
      if (onBlockedClick) onBlockedClick();
      return;
    }
    if (beforeStart) beforeStart();
    startGoogleSignIn(locationState);
  };
  return (
    <div>
      <div className="flex items-center my-4" aria-hidden="true">
        <div className="flex-1 border-t border-gray-300" />
        <span className="px-3 text-xs text-gray-500 uppercase tracking-wider">or</span>
        <div className="flex-1 border-t border-gray-300" />
      </div>
      <button
        type="button"
        data-testid="google-signin"
        onClick={handleClick}
        aria-disabled={blocked}
        title={blocked ? "Agree to the Terms of Service and Privacy Policy first" : undefined}
        className={`group relative w-full flex justify-center items-center gap-3 py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500${blocked ? " opacity-50 cursor-not-allowed" : ""}`}
      >
        <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
          <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
          <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
          <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
          <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
        </svg>
        {label}
      </button>
    </div>
  );
}
